import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Drawer,
  TableSortLabel
} from '@mui/material';
import { Add as AddIcon, AutoAwesome as AutoAwesomeIcon, Edit as EditIcon } from '@mui/icons-material';
import { debounce } from 'lodash';
import GoalDrawer from './GoalDrawer';
import AIGoalSuggestions from './AIGoalSuggestions';
import CapabilitiesTable from './CapabilitiesTable';

export default function DevelopmentGoals({ 
  careerData, 
  onUpdate, 
  selectedMember, 
  onAddActionItem,
  selectedGoalId,
  onGoalSelected
}) {
  const [careerGoal, setCareerGoal] = useState(careerData?.careerGoal || '');
  const [nextRole, setNextRole] = useState(careerData?.nextRole || '');
  const [targetYear, setTargetYear] = useState(careerData?.targetYear || 1);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [newGoal, setNewGoal] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [sortDirection, setSortDirection] = useState('asc');

  // Create debounced update functions using useCallback
  const debouncedUpdate = useCallback(
    debounce((updates) => {
      onUpdate({ ...careerData, ...updates });
    }, 300),
    [careerData, onUpdate]
  );

  useEffect(() => {
    console.log('DevelopmentGoals: careerData updated', careerData);
    setCareerGoal(careerData?.careerGoal || '');
    setNextRole(careerData?.nextRole || '');
    setTargetYear(careerData?.targetYear || 1);
  }, [careerData]);

  useEffect(() => {
    if (selectedGoalId && careerData?.developmentGoals) {
      const goal = careerData.developmentGoals.find(g => g.id === selectedGoalId);
      if (goal) {
        setSelectedGoal(goal);
        setIsDrawerOpen(true);
        onGoalSelected();
      }
    }
  }, [selectedGoalId, careerData?.developmentGoals, onGoalSelected]);

  const handleSort = () => {
    const direction = sortDirection === 'asc' ? 'desc' : 'asc';
    const sortedGoals = [...(careerData.developmentGoals || [])].sort((a, b) => {
      const scoreA = a.score || 0;
      const scoreB = b.score || 0;
      return direction === 'asc' ? scoreA - scoreB : scoreB - scoreA;
    });
    onUpdate({
      ...careerData,
      developmentGoals: sortedGoals
    });
    setSortDirection(direction);
  };

  const handleCareerGoalChange = (event) => {
    const newValue = event.target.value;
    setCareerGoal(newValue);
    debouncedUpdate({ careerGoal: newValue });
  };

  const handleNextRoleChange = (event) => {
    const newValue = event.target.value;
    setNextRole(newValue);
    debouncedUpdate({ nextRole: newValue });
  };

  const handleYearChange = (event) => {
    const newValue = event.target.value;
    setTargetYear(newValue);
    onUpdate({ ...careerData, targetYear: newValue });
  };

  const handleGoalClick = (goal) => {
    setSelectedGoal(goal);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedGoal(null);
  };

  const handleNewGoalChange = (event) => {
    setNewGoal(event.target.value);
  };

  const handleNewGoalKeyPress = async (event) => {
    if (event.key === 'Enter' && newGoal.trim()) {
      const newGoalObj = {
        id: Date.now().toString(), // Simple unique ID
        goal: newGoal.trim(),
        actionItems: [],
        score: 0
      };

      const updatedGoals = [...(careerData.developmentGoals || []), newGoalObj];
      await onUpdate({
        ...careerData,
        developmentGoals: updatedGoals
      });

      setNewGoal('');
      setIsEditing(false);
    }
  };

  const handleGoalBlur = async () => {
    if (newGoal.trim()) {
      const newGoalObj = {
        id: Date.now().toString(),
        goal: newGoal.trim(),
        actionItems: [],
        score: 0
      };

      const updatedGoals = [...(careerData.developmentGoals || []), newGoalObj];
      await onUpdate({
        ...careerData,
        developmentGoals: updatedGoals
      });

      setNewGoal('');
      setIsEditing(false);
    }
  };

  const handleGoalUpdate = (updatedGoal) => {
    const updatedGoals = careerData.developmentGoals.map(g => 
      g.id === updatedGoal.id ? updatedGoal : g
    );
    onUpdate({
      ...careerData,
      developmentGoals: updatedGoals
    });
  };

  const handleGoalDelete = (goalId) => {
    const updatedGoals = careerData.developmentGoals.filter(g => g.id !== goalId);
    onUpdate({
      ...careerData,
      developmentGoals: updatedGoals
    });
  };

  const handleAIGoalSelect = (generatedGoal) => {
    const newGoalObj = {
      id: Date.now().toString(),
      ...generatedGoal
    };

    const updatedGoals = [...(careerData.developmentGoals || []), newGoalObj];
    onUpdate({
      ...careerData,
      developmentGoals: updatedGoals
    });
  };

  const handleAddActionItem = (goalId) => {
    onAddActionItem(goalId);
  };

  // Clean up debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  console.log('DevelopmentGoals rendering with:', {
    careerGoal,
    nextRole,
    targetYear,
    careerData
  });

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 3, pt:0 }}>
      {/* Title Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{
            fontSize: "24px",
            fontWeight: 500,
            color: '#000000',
            mb: 1
          }}
        >
          Development goals
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#666666',
            fontSize: '14px'
          }}
        >
          Identify goals that will help you achieve the next role
        </Typography>
      </Box>

      {/* Career Goal Section */}
      <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Career ambition</Typography>

        <TextField
          fullWidth
          placeholder="Define high level career ambition"
          value={careerGoal}
          onChange={handleCareerGoalChange}
          sx={{ mb: 3 }}
        />

        {/* Next Role and Year Section */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <Typography sx={{ alignSelf: 'center', fontWeight: 600 }}>Next role </Typography>
          
          <TextField
            placeholder="Your next role"
            value={nextRole}
            onChange={handleNextRoleChange}
            sx={{ 
                width: '300px'
             }}
          />
          <Typography sx={{ alignSelf: 'center' }}>in</Typography>
          <Select
            value={targetYear}
            onChange={handleYearChange}
            sx={{ width: 100 }}
          >
            {[1, 2, 3, 4, 5].map(year => (
              <MenuItem key={year} value={year}>{year} year</MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* Define Goals Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Define goals</Typography>
          <Button
            variant="text"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setIsAIDialogOpen(true)}
            sx={{
              ml: 4,
              backgroundColor: 'grey.100',
              color: 'text.primary',
              textTransform: 'none',
              padding: '6px 16px',
              border: '1px solid transparent',
              '&:hover': {
                backgroundColor: 'common.white',
                border: '1px solid',
                borderColor: 'primary.main',
              },
              '& .MuiSvgIcon-root': {
                fontSize: '1.2rem',
              }
            }}
          >
            Generate with AI
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f0f0f0' }}>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>Goal</TableCell>
                <TableCell align="center" style={{ width: '150px', fontWeight: 'bold' }}>Action items</TableCell>
                <TableCell align="right" style={{ width: '100px', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={true}
                    direction={sortDirection}
                    onClick={handleSort}
                  >
                    Score
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {careerData?.developmentGoals?.map((goal) => (
                <TableRow
                  key={goal.id}
                  onClick={() => handleGoalClick(goal)}
                  sx={{ 
                    cursor: 'pointer',
                    height: '10px',
                    '&:hover': {
                        backgroundColor: '#f4f4f4'
                      },
                    '&:hover .goal-chevron': {
                      opacity: 1,
                    }                    
                  }}
                >
                  <TableCell sx={{ paddingTop: '12px', paddingBottom: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {goal.goal}
                      <Box 
                        className="goal-chevron"
                        sx={{ 
                          opacity: 0,
                          transition: 'opacity 0.2s',
                         // backgroundColor: '#ffffff',
                          //border: '1px solid',
                        //borderColor: '#cbcbcb',
                          //borderRadius: '4px',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <EditIcon 
                          sx={{ 
                            fontSize: '20px',
                            color: 'action.active'
                          }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ paddingTop: '12px', paddingBottom: '12px' }}>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddActionItem(goal.id);
                      }}
                      sx={{
                        textTransform: 'none',
                        color: 'text.secondary',
                        backgroundColor: 'white',
                        borderRadius: '20px',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        border: '1px solid',
                        height: '42px',
                        width: '150px',
                        borderColor: '#cbcbcb',
                        '&:hover': {
                          backgroundColor: '#ffffff',
                          borderColor: '#2D68FE',
                          color: '#242424'
                        }
                      }}
                    >
                      {goal.actionItems?.length 
                        ? `${goal.actionItems.length} action items`
                        : 'Add action item'}
                    </Button>
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', paddingTop: '12px', paddingBottom: '12px' }}>
                    {goal.score.toFixed(1)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3} sx={{ paddingTop: '0px', paddingBottom: '0px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth
                    placeholder="Add new goal"
                    value={newGoal}
                    onChange={handleNewGoalChange}
                    onKeyPress={handleNewGoalKeyPress}
                    onBlur={handleGoalBlur}
                    sx={{
                        '& .MuiInput-underline:before': { borderBottom: 'none' },
                      '& .MuiInput-underline:hover:before': { borderBottom: '1px solid rgba(0, 0, 0, 0.42)' },
                        fontSize: '5px',
                        padding: '0px',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      }
                    }}
                  /></Box>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Optional Sections */}
      <Box sx={{ bgcolor: '#EFF6FF', p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Optional
        </Typography>
        
        {/* Current Capabilities Section */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          What are some of the capabilities you already excel at?
        </Typography>
        <CapabilitiesTable
          data={careerData?.currentCapabilities || []}
          onUpdate={(newData) => onUpdate({
            ...careerData,
            currentCapabilities: newData
          })}
          columnHeaders={['Capability', '1-2 skills you already excel in']}
          placeholder="Add new capability here"
        />

        {/* Development Areas Section */}
        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          To accomplish and reach your career goals, what are the developmental areas you can work on and improve?
        </Typography>
        <CapabilitiesTable
          data={careerData?.developmentAreas || []}
          onUpdate={(newData) => onUpdate({
            ...careerData,
            developmentAreas: newData
          })}
          columnHeaders={['Development area', '1-2 skills you will need to master']}
          placeholder="Add new development area here"
        />
      </Box>

      {/* Goal Details Drawer */}
      <GoalDrawer
        open={isDrawerOpen}
        goal={selectedGoal}
        onClose={handleDrawerClose}
        onUpdate={handleGoalUpdate}
        onDelete={handleGoalDelete}
        onAddActionItem={handleAddActionItem}
      />

      {/* Add the AI Suggestions Dialog */}
      <AIGoalSuggestions
        open={isAIDialogOpen}
        onClose={() => setIsAIDialogOpen(false)}
        onSelect={handleAIGoalSelect}
        jobFunction={selectedMember?.jobFunction}
        careerGoal={selectedMember?.careerGoal}
        nextRole={selectedMember?.nextRole}
      />
    </Box>
  );
}
